import { useEffect, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const TECHNICAL_DETAILS_EVENT = "silversage:technical-details";

export type TechnicalDetailsPayload = {
  what: string;
  details: string;
};

/**
 * Global "Show technical details" surface.
 *
 * Rendered at the application root so it always sits above admin dialogs and
 * overlays — the previous toast-inside-a-toast approach was intercepted by
 * modal overlays and appeared inert. Details are already redacted upstream in
 * friendlyError(); no tokens, keys, or signed credentials reach this view.
 */
export function TechnicalDetailsDialog() {
  const [payload, setPayload] = useState<TechnicalDetailsPayload | null>(null);

  useEffect(() => {
    const onEvent = (e: Event) => {
      const detail = (e as CustomEvent<TechnicalDetailsPayload>).detail;
      if (detail?.details) setPayload(detail);
    };
    window.addEventListener(TECHNICAL_DETAILS_EVENT, onEvent as EventListener);
    return () => window.removeEventListener(TECHNICAL_DETAILS_EVENT, onEvent as EventListener);
  }, []);

  const open = !!payload;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && setPayload(null)}>
      <DialogContent className="max-w-lg z-[2147483100]">
        <DialogHeader>
          <DialogTitle>Technical details</DialogTitle>
          <DialogDescription>{payload?.what}</DialogDescription>
        </DialogHeader>
        <pre className="max-h-72 overflow-auto whitespace-pre-wrap rounded-md border bg-muted/40 p-3 text-xs">
          {payload?.details}
        </pre>
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              if (!payload) return;
              void navigator.clipboard
                ?.writeText(`${payload.what}\n${payload.details}`)
                .then(() => toast.success("Copied"))
                .catch(() => toast.error("Couldn't copy"));
            }}
          >
            Copy
          </Button>
          <Button size="sm" onClick={() => setPayload(null)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default TechnicalDetailsDialog;
